import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
  TextField,
  Switch,
  Button,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { ProfileEditApi, ProfileView } from "../../Utility/UtilApi/UtilApi";
import { buttonColor } from "../../UtilColorPalate/UtilColorPalate";

const ProfileModal = ({ open, handleClose }) => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editValues, setEditValues] = useState({});
  const [editableFields, setEditableFields] = useState({});
  const [changedFields, setChangedFields] = useState(new Set()); // Track changed fields
  const [existingAddresses, setExistingAddresses] = useState([]);

  useEffect(() => {
    if (open) {
      fetchProfile();
    }
  }, [open]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await ProfileView();
      const profile = response.data.profile;
      const addresses = response.data.addresses || [];
  
      const billingAddressFromData = addresses.find(addr => addr.type?.name === "Billing") || {};
  
      const initialData = {
        ...profile,
        billingAddress: {
          line1: billingAddressFromData.addresslineone || "",
          line2: billingAddressFromData.addresslinetwo || "",
          city: billingAddressFromData.city || "",
          province: billingAddressFromData.province || "",
          country: billingAddressFromData.country || "",
          postalCode: billingAddressFromData.postalcode || "",
        },
      };

      setProfileData(initialData);
      setEditValues(initialData);
      setExistingAddresses(addresses);
      setEditableFields({});
      setChangedFields(new Set()); // Reset changed fields on fetch
    } catch (error) {
      console.error("Error fetching profile:", error);
      setProfileData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (field) => {
    setEditableFields((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleChange = (field, value) => {
    setEditValues((prev) => ({
      ...prev,
      [field]: value,
      name: `${prev.firstname || ""} ${prev.lastname || ""}`.trim(),
    }));
    setChangedFields((prev) => new Set(prev).add(field)); // Mark field as changed
  };

  const handleAddressChange = (field, value) => {
    setEditValues((prev) => ({
      ...prev,
      billingAddress: {
        ...prev.billingAddress,
        [field]: value,
      },
    }));
    setChangedFields((prev) => new Set(prev).add(`billingAddress.${field}`)); // Mark address field as changed
  };

  const handleSaveChanges = async () => {
    try {
      const existingBillingAddress = existingAddresses.find(addr => addr.type?.name === "Billing");

      // Construct minimal profile payload with only changed fields
      const profilePayload = {};
      if (changedFields.has("firstname")) profilePayload.firstname = editValues.firstname || "";
      if (changedFields.has("lastname")) profilePayload.lastname = editValues.lastname || "";
      if (changedFields.has("emailoptout")) profilePayload.emailoptout = Boolean(editValues.emailoptout);
      if (changedFields.has("smsoptout")) profilePayload.smsoptout = Boolean(editValues.smsoptout);
      if (changedFields.has("calloptout")) profilePayload.calloptout = Boolean(editValues.calloptout);
      if (changedFields.has("consentprovided")) profilePayload.consentprovided = Boolean(editValues.consentprovided);
      if (changedFields.has("firstname") || changedFields.has("lastname")) {
        profilePayload.name = `${editValues.firstname || ""} ${editValues.lastname || ""}`.trim();
      }

      // Construct minimal billing address payload with only changed fields
      const billingAddressPayload = {};
      if (existingBillingAddress) billingAddressPayload.recordid = existingBillingAddress.recordid || "";
      else billingAddressPayload.name = "Billing Address";
      billingAddressPayload.type = "Billing";

      if (changedFields.has("billingAddress.line1")) billingAddressPayload.addresslineone = editValues.billingAddress.line1 || "";
      if (changedFields.has("billingAddress.line2")) billingAddressPayload.addresslinetwo = editValues.billingAddress.line2 || "";
      if (changedFields.has("billingAddress.city")) billingAddressPayload.city = editValues.billingAddress.city || "";
      if (changedFields.has("billingAddress.province")) billingAddressPayload.province = editValues.billingAddress.province || "";
      if (changedFields.has("billingAddress.country")) billingAddressPayload.country = editValues.billingAddress.country || "";
      if (changedFields.has("billingAddress.postalCode")) billingAddressPayload.postalcode = editValues.billingAddress.postalCode || "";

      // Only include addresses if any billing address field was changed
      const hasAddressChanges = Array.from(changedFields).some(field => field.startsWith("billingAddress."));
      const updatedProfile = {
        profile: Object.keys(profilePayload).length > 0 ? profilePayload : undefined,
        addresses: hasAddressChanges ? [billingAddressPayload] : undefined,
      };

      // Remove undefined properties
      if (!updatedProfile.profile) delete updatedProfile.profile;
      if (!updatedProfile.addresses) delete updatedProfile.addresses;

      console.log("Sending payload:", updatedProfile); // For debugging

      if (Object.keys(updatedProfile).length === 0) {
        handleClose(); // Nothing to update
        
      }

      await ProfileEditApi(updatedProfile);
      
      // Update local state with only the changed values
      setProfileData((prev) => ({
        ...prev,
        ...profilePayload,
        billingAddress: {
          ...prev.billingAddress,
          ...(changedFields.has("billingAddress.line1") && { line1: editValues.billingAddress.line1 }),
          ...(changedFields.has("billingAddress.line2") && { line2: editValues.billingAddress.line2 }),
          ...(changedFields.has("billingAddress.city") && { city: editValues.billingAddress.city }),
          ...(changedFields.has("billingAddress.province") && { province: editValues.billingAddress.province }),
          ...(changedFields.has("billingAddress.country") && { country: editValues.billingAddress.country }),
          ...(changedFields.has("billingAddress.postalCode") && { postalCode: editValues.billingAddress.postalCode }),
        },
      }));
      handleClose();
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  const handleCancel = () => {
    setEditValues(profileData);
    setChangedFields(new Set()); // Reset changed fields on cancel
    handleClose();
  };

  const renderEditableField = (label, field, value) => (
    <Grid item xs={12} sx={{ display: "flex", alignItems: "center", gap: 1, my: 0.5 }}>
      <Typography sx={{ fontWeight: "bold", fontSize: "14px", color: "#444", minWidth: "100px" }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
        {editableFields[field] ? (
          <TextField
            value={value || ""}
            onChange={(e) => (field.startsWith("billingAddress.") ? handleAddressChange(field.replace("billingAddress.", ""), e.target.value) : handleChange(field, e.target.value))}
            variant="outlined"
            size="small"
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                height: "32px",
              },
            }}
          />
        ) : (
          <Typography sx={{ flex: 1, minHeight: "32px", display: "flex", alignItems: "center", fontSize: "14px" }}>
            {value || "--"}
          </Typography>
        )}
        <IconButton size="small" onClick={() => handleEditClick(field)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    </Grid>
  );

  const renderSwitch = (label, field) => (
    <Grid item xs={6} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", my: 0.5 }}>
      <Typography sx={{ fontWeight: "bold", fontSize: "14px", color: "#444" }}>{label}</Typography>
      <Switch
        checked={Boolean(editValues[field])}
        onChange={(e) => handleChange(field, e.target.checked)}
        size="small"
      />
    </Grid>
  );

  return (
    <Modal open={open} onClose={handleCancel}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 450,
          bgcolor: "white",
          boxShadow: 24,
          borderRadius: 3,
          p: 2,
        }}
      >
        <IconButton onClick={handleCancel} sx={{ position: "absolute", top: 5, right: 5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>

        {loading ? (
          <CircularProgress sx={{ display: "block", mx: "auto", my: 2 }} />
        ) : profileData ? (
          <>
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Avatar
                sx={{
                  width: 70,
                  height: 70,
                  margin: "auto",
                  mb: 1,
                  border: "2px solid #ddd",
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "#222", fontSize: "18px" }}>
                {profileData.firstname} {profileData.lastname}
              </Typography>
            </Box>

            <Grid container spacing={1}>
              {renderEditableField("First Name", "firstname", editValues.firstname)}
              {renderEditableField("Last Name", "lastname", editValues.lastname)}

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#222", mt: 1, mb: 0.5 }}>
                  Billing Address
                </Typography>
              </Grid>
              {renderEditableField("Address Line One", "billingAddress.line1", editValues.billingAddress?.line1)}
              {renderEditableField("Address Line Two", "billingAddress.line2", editValues.billingAddress?.line2)}
              {renderEditableField("City", "billingAddress.city", editValues.billingAddress?.city)}
              {renderEditableField("Province", "billingAddress.province", editValues.billingAddress?.province)}
              {renderEditableField("Country", "billingAddress.country", editValues.billingAddress?.country)}
              {renderEditableField("Postal Code", "billingAddress.postalCode", editValues.billingAddress?.postalCode)}

              {renderSwitch("Email Opt-out", "emailoptout")}
              {renderSwitch("SMS Opt-out", "smsoptout")}
              {renderSwitch("Call Opt-out", "calloptout")}
              {renderSwitch("Consent Provided", "consentprovided")}
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
              <Button variant="outlined" color="error" onClick={handleCancel} sx={{ width: "48%", py: 0.5 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveChanges}
                sx={{ width: "48%", py: 0.5 }}
                style={{
                  color: "black",
                  backgroundColor: buttonColor,
                }}
              >
                Save
              </Button>
            </Box>
          </>
        ) : (
          <Typography color="error" sx={{ textAlign: "center", py: 2 }}>
            Failed to load profile
          </Typography>
        )}
      </Box>
    </Modal>
  );
};

export default ProfileModal;